import React from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, COLORS, formatNumber, formatDate, tooltipStyle } from '../types';

interface UserJourneyTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = [
  'user-journey', 'screen-engagement', 'tab-distribution', 'session-stats',
  'dau-history', 'reg-funnel', 'onboarding-funnel', 'permission-rates',
  'feature-engagement', 'feature-daily-trend', 'active-users',
  'follow-activity', 'call-activity',
];

const UserJourneyTab: React.FC<UserJourneyTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  if (loading) return <LoadingSpinner />;

  const journey = data['user-journey'] || {};
  const signupFunnel: any[] = journey.signupFunnel || [];
  const journeyDetailFunnel: any[] = journey.journeyDetailFunnel || [];
  const leaderboardFunnel: any[] = journey.leaderboardFunnel || [];
  const aiLensFunnel: any[] = journey.aiLensFunnel || [];
  const quizGoals: any[] = journey.quizGoals || [];
  const quizSteps: any[] = journey.quizSteps || [];
  const destinations: any[] = journey.destinations || [];
  const paywallPages: any[] = journey.paywallPages || [];
  const paywallSkips: any[] = journey.paywallSkips || [];
  const dailyJourney: any[] = journey.dailyJourney || [];
  const quizSkips: any[] = journey.quizSkips || [];
  const followDetail: any = journey.followDetail || [];
  const paywallErrors: any[] = journey.paywallErrors || [];
  const paywallSkipDetail: any[] = journey.paywallSkipDetail || [];
  const paywallConversionBySource: any[] = journey.paywallConversionBySource || [];

  // Build signup funnel with drop-off %
  const signupFunnelWithDropoff = signupFunnel.map((s: any, i: number) => {
    const prev = i > 0 ? signupFunnel[i - 1] : null;
    const dropoff = prev && prev.users > 0 ? ((1 - s.users / prev.users) * 100).toFixed(1) : null;
    return { ...s, dropoff };
  });

  // Build journey detail funnel with drop-off %
  const journeyDetailWithDropoff = journeyDetailFunnel.map((s: any, i: number) => {
    const prev = i > 0 ? journeyDetailFunnel[i - 1] : null;
    const dropoff = prev && prev.users > 0 ? ((1 - s.users / prev.users) * 100).toFixed(1) : null;
    return { ...s, dropoff };
  });

  // Daily chart: pivot by event
  const dailyMap: Record<string, any> = {};
  for (const d of dailyJourney) {
    if (!dailyMap[d.day]) dailyMap[d.day] = { day: formatDate(d.day) };
    dailyMap[d.day][d.event] = d.uniqueUsers;
  }
  const dailyChart = Object.values(dailyMap).sort((a: any, b: any) => a.day.localeCompare(b.day));

  // Paywall page funnel by source
  const signupPages = paywallPages.filter((p: any) => p.source === 'signup').sort((a: any, b: any) => a.page - b.page);

  // Quiz skip total
  const totalQuizSkips = quizSkips.reduce((sum: number, s: any) => sum + s.count, 0);

  // Pivot paywallConversionBySource into per-source rows
  const conversionMap: Record<string, any> = {};
  for (const r of paywallConversionBySource) {
    if (!conversionMap[r.source]) conversionMap[r.source] = { source: r.source, views: 0, checkouts: 0, success: 0, fails: 0, skips: 0 };
    const row = conversionMap[r.source];
    if (r.event === 'trial_paywall_view') row.views = r.uniqueUsers;
    else if (r.event === 'trial_paywall_checkout_start') row.checkouts = r.uniqueUsers;
    else if (r.event === 'trial_paywall_checkout_success') row.success = r.uniqueUsers;
    else if (r.event === 'trial_paywall_checkout_fail') row.fails = r.uniqueUsers;
    else if (r.event === 'journey_paywall_skip') row.skips = r.uniqueUsers;
  }
  const conversionRows = Object.values(conversionMap).sort((a: any, b: any) => b.views - a.views);

  // Normalize followDetail to array
  const followDetailArr = Array.isArray(followDetail) ? followDetail :
    [{ action: 'completed', ...followDetail.completed }, { action: 'skipped', ...followDetail.skipped }].filter((f: any) => f.count > 0 || f.uniqueUsers > 0);

  return (
    <div className="space-y-6">
      {/* Top stats — use paywall funnel (reliable old events) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Onboarded" value={signupFunnel.find((s: any) => s.step === 'username_form')?.users || 0} subtitle="Username form (30d)" />
        <StatCard title="Paywall Viewed" value={signupFunnel.find((s: any) => s.step === 'paywall_view')?.users || 0} subtitle="Saw trial paywall" />
        <StatCard title="Checkout Started" value={signupFunnel.find((s: any) => s.step === 'paywall_checkout')?.users || 0} subtitle="Started payment" />
        <StatCard title="Subscribed" value={signupFunnel.find((s: any) => s.step === 'paywall_success')?.users || 0}
          subtitle={`${signupFunnel.find((s: any) => s.step === 'paywall_view')?.users ? ((signupFunnel.find((s: any) => s.step === 'paywall_success')?.users / signupFunnel.find((s: any) => s.step === 'paywall_view')?.users) * 100).toFixed(1) : 0}% of views`} />
        <StatCard title="Skipped Paywall" value={signupFunnel.find((s: any) => s.step === 'paywall_skip')?.users || 0} subtitle="Closed paywall" />
      </div>

      {/* Paywall Conversion Funnel (reliable data) */}
      <ChartCard title="Paywall Conversion Funnel (30 days)">
        <div className="space-y-2">
          {signupFunnelWithDropoff.map((step: any) => {
            const maxUsers = signupFunnel[0]?.users || 1;
            const pct = maxUsers > 0 ? (step.users / maxUsers) * 100 : 0;
            return (
              <div key={step.step} className="flex items-center gap-3">
                <div className="w-40 text-xs text-gray-400 text-right truncate">{step.label}</div>
                <div className="flex-1 h-8 bg-white/5 rounded-lg overflow-hidden relative">
                  <div className="h-full bg-gradient-to-r from-[#FF9C15] to-[#F42026] rounded-lg transition-all" style={{ width: `${Math.max(pct, 1)}%` }} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-white font-medium">
                    {formatNumber(step.users)} users
                  </span>
                </div>
                <div className="w-16 text-xs text-right">
                  {step.dropoff ? <span className="text-red-400">-{step.dropoff}%</span> : <span className="text-gray-600">--</span>}
                </div>
              </div>
            );
          })}
        </div>
      </ChartCard>

      {/* Detailed Journey Funnel (new tracking) */}
      <ChartCard title="Detailed Journey Funnel (new app versions only)">
        {journeyDetailFunnel.some((s: any) => s.users > 0) ? (
          <div className="space-y-2">
            {journeyDetailWithDropoff.map((step: any) => {
              const maxUsers = journeyDetailFunnel[0]?.users || 1;
              const pct = maxUsers > 0 ? (step.users / maxUsers) * 100 : 0;
              return (
                <div key={step.step} className="flex items-center gap-3">
                  <div className="w-40 text-xs text-gray-400 text-right truncate">{step.label}</div>
                  <div className="flex-1 h-8 bg-white/5 rounded-lg overflow-hidden relative">
                    <div className="h-full bg-gradient-to-r from-[#38bdf8] to-[#818cf8] rounded-lg transition-all" style={{ width: `${Math.max(pct, 1)}%` }} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-white font-medium">
                      {formatNumber(step.users)} users
                    </span>
                  </div>
                  <div className="w-16 text-xs text-right">
                    {step.dropoff ? <span className="text-red-400">-{step.dropoff}%</span> : <span className="text-gray-600">--</span>}
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-gray-500 mt-2">Note: These events only fire from new app versions. Numbers will grow as users update.</p>
          </div>
        ) : <p className="text-gray-500 text-sm">Waiting for users on new app version</p>}
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quiz Goal Distribution */}
        <ChartCard title="Quiz Primary Goal Distribution">
          {quizGoals.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={quizGoals.map((g: any) => ({ name: g.goal || 'unknown', value: g.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {quizGoals.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-sm">No data yet</p>}
        </ChartCard>

        {/* First Destination Distribution */}
        <ChartCard title="First Destination After Onboarding">
          {destinations.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={destinations.map((d: any) => ({ name: d.destination || 'unknown', value: d.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {destinations.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-sm">No data yet</p>}
        </ChartCard>
      </div>

      {/* Quiz Skip Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title={`Quiz Skip by Step (${formatNumber(totalQuizSkips)} total skips)`}>
          {quizSkips.length > 0 ? (
            <div className="space-y-2">
              {quizSkips.map((s: any) => {
                const maxCount = quizSkips[0]?.count || 1;
                const pct = (s.count / maxCount) * 100;
                return (
                  <div key={s.stepId} className="flex items-center gap-2">
                    <div className="w-28 text-xs text-gray-400 truncate text-right" title={s.stepId}>{s.stepId}</div>
                    <div className="flex-1 h-6 bg-white/5 rounded overflow-hidden relative">
                      <div className="h-full bg-red-500/60 rounded" style={{ width: `${pct}%` }} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white">
                        step {s.stepIndex}/{s.totalSteps} — {formatNumber(s.count)} skips ({formatNumber(s.uniqueUsers)} users)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-gray-500 text-sm">No quiz skip data yet</p>}
        </ChartCard>

        {/* Quiz Step Engagement */}
        <ChartCard title="Quiz Step Engagement (Top Steps)">
          {quizSteps.length > 0 ? (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {quizSteps.slice(0, 15).map((s: any) => {
                const maxCount = quizSteps[0]?.uniqueUsers || 1;
                const pct = (s.uniqueUsers / maxCount) * 100;
                return (
                  <div key={s.stepId} className="flex items-center gap-2">
                    <div className="w-32 text-xs text-gray-400 truncate text-right" title={s.stepId}>{s.stepId}</div>
                    <div className="flex-1 h-5 bg-white/5 rounded overflow-hidden">
                      <div className="h-full bg-[#FF9C15]/60 rounded" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-12 text-xs text-gray-300 text-right">{s.uniqueUsers}</div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-gray-500 text-sm">No data yet</p>}
        </ChartCard>
      </div>

      {/* Paywall Page Drop-off (Signup Flow) */}
      <ChartCard title="Trial Paywall Page Drop-off (Signup Flow)">
        {signupPages.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={signupPages.map((p: any) => ({ name: p.page === 0 ? 'Features' : p.page === 1 ? 'Plans' : `Page ${p.page}`, users: p.uniqueUsers, events: p.count }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" stroke="#888" fontSize={12} />
              <YAxis stroke="#888" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="users" fill="#FF9C15" radius={[6, 6, 0, 0]} name="Unique Users" />
            </BarChart>
          </ResponsiveContainer>
        ) : <p className="text-gray-500 text-sm">No data yet — paywall page tracking will appear after deployment</p>}
      </ChartCard>

      {/* Paywall Conversion by Source */}
      <ChartCard title="Paywall Conversion Rate by Source">
        {conversionRows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-white/10">
                  <th className="text-left py-2 px-3">Source</th>
                  <th className="text-right py-2 px-3">Views</th>
                  <th className="text-right py-2 px-3">Checkouts</th>
                  <th className="text-right py-2 px-3">Success</th>
                  <th className="text-right py-2 px-3">Fails</th>
                  <th className="text-right py-2 px-3">Skips</th>
                  <th className="text-right py-2 px-3">Conv. Rate</th>
                </tr>
              </thead>
              <tbody>
                {conversionRows.map((r: any) => {
                  const convRate = r.views > 0 ? ((r.success / r.views) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={r.source} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 px-3 text-white font-medium">{r.source}</td>
                      <td className="py-2 px-3 text-right text-gray-300">{formatNumber(r.views)}</td>
                      <td className="py-2 px-3 text-right text-gray-300">{formatNumber(r.checkouts)}</td>
                      <td className="py-2 px-3 text-right text-green-400">{formatNumber(r.success)}</td>
                      <td className="py-2 px-3 text-right text-red-400">{formatNumber(r.fails)}</td>
                      <td className="py-2 px-3 text-right text-yellow-400">{formatNumber(r.skips)}</td>
                      <td className="py-2 px-3 text-right">
                        <span className={`font-bold ${parseFloat(convRate) > 5 ? 'text-green-400' : parseFloat(convRate) > 1 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {convRate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <p className="text-gray-500 text-sm">No conversion data yet</p>}
      </ChartCard>

      {/* Paywall Skip Detail + Paywall Errors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Paywall Skip Detail by Source">
          {paywallSkipDetail.length > 0 ? (
            <div className="space-y-2">
              {paywallSkipDetail.map((s: any) => (
                <div key={s.source} className="p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-white">{s.source}</span>
                    <span className="text-sm text-gray-300">{formatNumber(s.uniqueUsers)} users</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span>Page: {s.avgPage?.toFixed(1) ?? '—'}</span>
                    <span>Avg time: {s.avgTimeSpentMs ? `${(s.avgTimeSpentMs / 1000).toFixed(1)}s` : '—'}</span>
                    <span>{formatNumber(s.count)} skips</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500 text-sm">No skip detail yet</p>}
        </ChartCard>

        <ChartCard title="Payment Errors">
          {paywallErrors.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {paywallErrors.map((e: any, i: number) => (
                <div key={i} className="p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-red-400">{e.errorCode || 'unknown'}</span>
                    <span className="text-sm text-white font-bold">{formatNumber(e.count)} errors</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span>Plan: {e.plan || '—'}</span>
                    <span>Source: {e.source || '—'}</span>
                    <span>{formatNumber(e.uniqueUsers)} users affected</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500 text-sm">No payment errors (good!)</p>}
        </ChartCard>
      </div>

      {/* Follow Step Detail */}
      <ChartCard title="Mutual Friends Follow Step">
        {followDetailArr.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {followDetailArr.map((f: any) => (
              <div key={f.action} className="p-4 bg-white/5 rounded-xl text-center">
                <div className="text-2xl font-bold text-white mb-1">{formatNumber(f.uniqueUsers)}</div>
                <div className="text-sm text-gray-400 mb-2">{f.action === 'completed' ? 'Followed & Continued' : f.action === 'skipped' ? 'Skipped' : f.action}</div>
                {f.avgFollowed != null && (
                  <div className="text-xs text-gray-500">Avg followed: {f.avgFollowed.toFixed(1)} people</div>
                )}
              </div>
            ))}
          </div>
        ) : <p className="text-gray-500 text-sm">No follow step data yet</p>}
      </ChartCard>

      {/* Paywall Skip by Source (legacy simple view) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Paywall Skips by Source (Simple)">
          {paywallSkips.length > 0 ? (
            <div className="space-y-2">
              {paywallSkips.map((s: any) => (
                <div key={s.source} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-sm text-gray-300">{s.source}</span>
                  <span className="text-sm font-medium text-white">{formatNumber(s.uniqueUsers)} users ({formatNumber(s.count)} events)</span>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500 text-sm">No data yet</p>}
        </ChartCard>

        {/* Leaderboard Onboarding Funnel */}
        <ChartCard title="Leaderboard Onboarding Funnel">
          {leaderboardFunnel.some((s: any) => s.users > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={leaderboardFunnel} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#888" fontSize={12} />
                <YAxis dataKey="label" type="category" stroke="#888" fontSize={12} width={120} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="users" fill="#38bdf8" radius={[0, 6, 6, 0]} name="Users" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-sm">No data yet</p>}
        </ChartCard>
      </div>

      {/* AI Lens Onboarding Funnel */}
      <ChartCard title="AI Lens Onboarding Funnel">
        {aiLensFunnel.some((s: any) => s.users > 0) ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={aiLensFunnel} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" stroke="#888" fontSize={12} />
              <YAxis dataKey="label" type="category" stroke="#888" fontSize={12} width={120} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="users" fill="#34d399" radius={[0, 6, 6, 0]} name="Users" />
            </BarChart>
          </ResponsiveContainer>
        ) : <p className="text-gray-500 text-sm">No data yet</p>}
      </ChartCard>

      {/* Daily Journey Trends */}
      <ChartCard title="Daily Journey Trends (30 days)">
        {dailyChart.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" stroke="#888" fontSize={11} />
              <YAxis stroke="#888" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              <Line type="monotone" dataKey="journey_quiz_start" stroke="#FF9C15" strokeWidth={2} dot={false} name="Quiz Start" />
              <Line type="monotone" dataKey="journey_quiz_complete" stroke="#34d399" strokeWidth={2} dot={false} name="Quiz Complete" />
              <Line type="monotone" dataKey="journey_quiz_skip" stroke="#ef4444" strokeWidth={2} dot={false} name="Quiz Skip" />
              <Line type="monotone" dataKey="journey_destination" stroke="#38bdf8" strokeWidth={2} dot={false} name="Destination Reached" />
            </LineChart>
          </ResponsiveContainer>
        ) : <p className="text-gray-500 text-sm">No data yet</p>}
      </ChartCard>
    </div>
  );
};

export default UserJourneyTab;
